import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import ScrollToTop from "@/components/ScrollToTop";

// Ad traffic lands here, so the campaign pages ship in the first chunk.
import WebinarLanding from "@/pages/WebinarLanding";
import ThankYou from "@/pages/ThankYou";

// Everything else (and the site chrome) loads on demand.
const Navbar = lazy(() => import("@/components/Navbar"));
const Footer = lazy(() => import("@/components/Footer"));
const FloatingCTA = lazy(() => import("@/components/FloatingCTA"));
const ChatBot = lazy(() => import("@/components/ChatBot"));
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Courses = lazy(() => import("@/pages/Courses"));
const CourseDetail = lazy(() => import("@/pages/CourseDetail"));
const Webinars = lazy(() => import("@/pages/Webinars"));
const About = lazy(() => import("@/pages/About"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Refund = lazy(() => import("@/pages/Refund"));
const ShareStory = lazy(() => import("@/pages/ShareStory"));
const Assessment = lazy(() => import("@/pages/Assessment"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

function Shell() {
  // Webinar landing pages and the registration thank-you page are standalone campaign pages:
  // no nav, footer, floating CTA or chatbot, so registrants never wander into the paid catalogue.
  const path = useLocation().pathname;
  const standalone = path.startsWith('/w/') || (path.startsWith('/thank-you/') && path !== '/thank-you/payment');
  const noChat = standalone || path === '/';
  // The floating CTA bar distracts from the assessment flow.
  const noFloatingCta = standalone || path.startsWith('/7-min-future-readiness');

  return (
    <div className="min-h-screen ambient-page">
      <ScrollToTop />
      <Suspense fallback={null}>
        {!standalone && <Navbar />}
      </Suspense>
      <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:id" element={<CourseDetail />} />
          <Route path="/webinars" element={<Webinars />} />
          <Route path="/w/:slug" element={<WebinarLanding />} />
          <Route path="/thank-you/:kind" element={<ThankYou />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/share-your-story" element={<ShareStory />} />
          <Route path="/7-min-future-readiness" element={<Assessment />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        {!standalone && <Footer />}
        {!noFloatingCta && <FloatingCTA />}
        {!noChat && <ChatBot />}
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <Shell />
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
